import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { push, go } from "connected-react-router";
import {
  signIn,
  fetchAuthSession,
  resendSignUpCode,
  confirmSignUp,
  confirmSignIn,
} from "aws-amplify/auth";

import {
  emailValidator,
  FixedTitle,
  HttpMethods,
  mapCountry,
  required,
} from "@tphglobal/common/utils";
import {
  Button,
  Form,
  FormError,
  FormRow,
  FormRowItem,
  MaterialAutocompleteInput,
  Modal,
  PasswordInput,
  TextInput,
  Toast,
  DownloadAppBtn,
} from "@tphglobal/components";
import { useFormReducer, usePopupReducer } from "@tphglobal/common/hooks";
import useWindowSize from "@tphglobal/common/hooks/windowSize";
import { breakpoints } from "@tphglobal/common/theme/style.layout";
import { brand, greyScaleColour } from "@tphglobal/common/theme/style.palette";
import { routes } from "@tphglobal/freelance-portal/utils/routes";
import {
  mapIdNameToOption,
  regions,
  RegionTypes,
} from "@tphglobal/common/utils/constants";
import { apiCall } from "@tphglobal/common/redux/actions";
import { toast } from "react-toastify";
import { Container, TermsLinks } from "../../components";
import configAmplify from "../../config/awsConfig";
import SidePanel from "./sidePanel";
import { identifyUser } from "../../utils/posthogUtils";
import {
  NoAccountTextContainer,
  OrLogin,
  StyledAuthSignup,
  StyledButton,
  StyledContent,
  StyledContentContainer,
  StyledFormContainer,
  StyledFormDetails,
  StyledFormHeading,
  StyledHeaderContainer,
  StyledIcon,
  StyledInfoContainer,
  StyledLine,
  StyledLink,
  StyledLogo,
  StyledScreenWrapper,
  StyledSidePanelLowerTextMobile,
  StyledSmallLogo,
} from "./styles";
import messages from "../../messages";
import { UPDATE_LOGIN_AU, UPDATE_LOGIN_UK } from "../../api";
import { postLogin, setRegion } from "../../redux/actions";
import ConfirmOtpForm from "./ConfirmOtpForm";
import ErrorHandler from "../../utils/errorHandler";
import { downloadAppLink, EXTERNAL_LINKS } from "../../utils/constant";
import { getToken } from "../../redux/reducers/auth";

interface LoginResponseResult {
  [key: string]: string;
}

interface LoginResponse {
  result: [LoginResponseResult];
  status: boolean;
}

interface SubmitData {
  region: { id: RegionTypes; label: RegionTypes };
  email: string;
  password: string;
}

const validators = {
  region: [required(messages?.general?.validations?.regionRequired)],
  email: [
    required(messages?.general?.validations?.emailRequired),
    emailValidator,
  ],
  password: [required(messages?.general?.validations?.passwordRequired)],
};

const Login = () => {
  const {
    submitting,
    submitError,
    handleSubmit,
    connectField,
    setSubmitError,
    formValues,
  } = useFormReducer(validators);
  const reduxDispatch = useDispatch();
  const { width } = useWindowSize();
  const [error, setError] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const isMobile = width <= breakpoints.mobile;

  const {
    visibility: otpFormVisibility,
    showPopup: showOtpInputScreen,
    hidePopup: hideOtpForm,
    metaData: otpFormMetadata,
  } = usePopupReducer<{ data: SubmitData }>();

  const {
    visibility: totpFormVisibility,
    showPopup: showTotpInputScreen,
    hidePopup: hideTotpForm,
    metaData: totpFormMetadata,
  } = usePopupReducer<{ data: SubmitData }>();

  const {
    visibility: smsFormVisibility,
    showPopup: showSmsInputScreen,
    hidePopup: hideSmsForm,
    metaData: smsFormMetadata,
  } = usePopupReducer<{ data: SubmitData }>();

  useEffect(() => {
    const region = localStorage.getItem("region");
    const token = getToken();

    reduxDispatch(postLogin({ token, region: region as RegionTypes }));
  }, []);

  const updateLogin = async (email: string) =>
    new Promise<LoginResponse>((resolve, reject) => {
      try {
        const sanitizedBody = {
          userEmail: email,
        };
        const apiUrl =
          formValues?.region?.value?.id === RegionTypes.AU
            ? UPDATE_LOGIN_AU
            : UPDATE_LOGIN_UK;

        reduxDispatch(
          apiCall(
            apiUrl,
            resolve,
            (err) => {
              toast(<Toast type="error" text={ErrorHandler(err?.messgae)} />);
            },
            HttpMethods.PUT,
            sanitizedBody,
          ),
        );
      } catch (error) {
        toast(
          <Toast
            type="error"
            text={`POST call failed: ${JSON.parse(error.response.body)}`}
          />,
        );
      }
    });

  const loginUser = async (data?: SubmitData) => {
    hideOtpForm();

    const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();

    if (idToken) {
      reduxDispatch(postLogin({ token: idToken, region: data?.region?.id }));
    }
    localStorage.removeItem("justSignedUp");
    const user = await updateLogin(data?.email);

    if (user?.status) {
      // Set PostHog person properties for user tracking
      identifyUser(data.email);
    } else {
      toast(
        <Toast type="error" text="Unsuccessful login. Please try again." />,
      );
    }
  };

  const onSubmit = async (data: SubmitData) => {
    const sanitizedData = {
      ...data,
      email: data?.email?.toLowerCase()?.trim() || "",
    };

    // Clear any existing errors
    setSubmitError("");

    localStorage.setItem("region", sanitizedData.region?.id || "");

    configAmplify(sanitizedData.region?.id);

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: sanitizedData.email,
        password: sanitizedData.password,
        options: {
          userAttributes: {
            region: JSON.stringify(sanitizedData.region),
          },
        },
      });

      if (isSignedIn) {
        loginUser(sanitizedData);
      } else if (nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        // User needs to confirm sign up
        setUsername(sanitizedData?.email);
        await resendSignUpCode({
          username: sanitizedData?.email,
        });
        showOtpInputScreen({ data: sanitizedData });
      } else if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        showTotpInputScreen({ data: sanitizedData });
      } else if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        showSmsInputScreen({ data: sanitizedData });
      }
    } catch (error) {
      if (error?.name === "UserAlreadyAuthenticatedException") {
        reduxDispatch(go(0));
        return;
      }
      setSubmitError(ErrorHandler(error?.name));
    }
  };

  const handleOtpConfirmation = async (confirmationCode: string) => {
    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: otpFormMetadata?.data?.email,
        confirmationCode,
      });

      if (isSignUpComplete) {
        await signIn({
          username: otpFormMetadata?.data?.email,
          password: otpFormMetadata?.data?.password,
        });
        localStorage.removeItem("justSignedUp");
        loginUser(otpFormMetadata?.data);
      }
    } catch (error) {
      toast(<Toast type="error" text="Invalid otp." />);
    }
  };

  const handleMfaVerification = async (
    mfaCode: string,
    userData: SubmitData,
  ) => {
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: mfaCode,
      });

      if (isSignedIn) {
        localStorage.removeItem("justSignedUp");
        loginUser(userData);
      }
    } catch (error) {
      const errorMessage =
        ErrorHandler(error?.name) || "Invalid MFA code. Please try again.";
      toast(<Toast type="error" text={errorMessage} />);
    }
  };
  return (
    <Container
      hideTabNavigation
      noBorderRadius
      hideSidebar
      noPadding
      hasHeader={false}
      noMargin
    >
      <StyledScreenWrapper>
        <StyledFormContainer>
          <StyledContentContainer>
            <StyledHeaderContainer>
              {isMobile ? (
                <StyledSmallLogo
                  src="/assets/images/smallLoginLogo.svg"
                  alt="smallLogo"
                />
              ) : (
                <StyledLogo src="/assets/images/loginLogo.svg" alt="logo" />
              )}
              <StyledInfoContainer>
                <StyledFormHeading>
                  {messages?.login?.heading}
                </StyledFormHeading>
                <FixedTitle
                  varient="Fixed14Regular"
                  color={greyScaleColour?.grey100}
                >
                  {messages?.login?.subHeading}
                </FixedTitle>
              </StyledInfoContainer>
            </StyledHeaderContainer>

            <StyledContent>
              <StyledFormDetails>
                {/* <StyledAuthSignup>
                  <StyledButton>
                    <StyledIcon
                      src='/assets/images/Facebook.svg'
                      alt='facebookIcon'
                    />
                    <FixedTitle varient='Fixed14Medium'>
                      {messages?.login?.form?.facebook}
                    </FixedTitle>
                  </StyledButton>
                  <StyledButton>
                    <StyledIcon
                      src='/assets/images/Google.svg'
                      alt='googleIcon'
                    />
                    <FixedTitle varient='Fixed14Medium'>
                      {messages?.login?.form?.google}
                    </FixedTitle>
                  </StyledButton>
                </StyledAuthSignup>
                <OrLogin>
                  <StyledLine />
                  <FixedTitle
                    varient='Fixed12Regular'
                    color={greyScaleColour?.grey90}
                  >
                    {messages?.login?.form?.orLogin}
                  </FixedTitle>
                  <StyledLine />
                </OrLogin> */}
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <FormRow>
                    <FormRowItem>
                      {connectField("region", {
                        label: messages?.login?.form?.region,
                        options: regions?.map(mapCountry),
                      })(MaterialAutocompleteInput)}
                    </FormRowItem>
                  </FormRow>
                  <FormRow>
                    <FormRowItem>
                      {connectField("email", {
                        label: messages?.login?.form?.email,
                      })(TextInput)}
                    </FormRowItem>
                  </FormRow>
                  <FormRow marginBottom="10px">
                    <FormRowItem>
                      {connectField("password", {
                        label: messages?.login?.form?.password,
                      })(PasswordInput)}
                    </FormRowItem>
                  </FormRow>
                  <FormRow>
                    <FormRowItem justifyContent="flex-end">
                      <StyledLink href={routes.forgotPassword}>
                        <FixedTitle
                          varient="Fixed14Regular"
                          color={greyScaleColour?.grey100}
                          style={{ cursor: "pointer" }}
                        >
                          {messages?.login?.form?.forgotPassword}
                        </FixedTitle>
                      </StyledLink>
                    </FormRowItem>
                  </FormRow>
                  {submitError && (
                    <FormRow>
                      <FormRowItem>
                        <FormError message={submitError} />
                      </FormRowItem>
                    </FormRow>
                  )}
                  <FormRow marginBottom="0">
                    <FormRowItem>
                      <Button
                        label={
                          messages?.login?.form?.[isMobile ? "signIn" : "logIn"]
                        }
                        type="submit"
                        variant="outlined"
                        color="primary"
                        size="large"
                        disabled={submitting}
                      />
                    </FormRowItem>
                  </FormRow>
                </Form>
                <FixedTitle
                  varient="Fixed14Regular"
                  textAlign="center"
                  color={greyScaleColour?.grey100}
                >
                  {messages?.login?.form?.tnC}
                  <TermsLinks
                    links={[
                      {
                        label: messages?.signup?.form?.termsOfUse,
                        href: EXTERNAL_LINKS?.termsOfUse,
                      },
                      {
                        label: messages?.signup?.form?.privacyPolicy,
                        href: EXTERNAL_LINKS?.privacyPolicy,
                      },
                    ]}
                  />
                </FixedTitle>
              </StyledFormDetails>
              <NoAccountTextContainer style={{ marginBottom: "10px" }}>
                <FixedTitle
                  varient="Fixed14Regular"
                  color={greyScaleColour?.grey100}
                >
                  {messages?.login?.form?.noAccount}
                </FixedTitle>
                <FixedTitle
                  style={{ cursor: "pointer" }}
                  varient="Fixed14Medium"
                  color={brand?.secondaryMain}
                  onClick={() => reduxDispatch(push(routes.signup))}
                >
                  {messages?.login?.form?.getStarted}
                </FixedTitle>
              </NoAccountTextContainer>
            </StyledContent>
            <StyledSidePanelLowerTextMobile>
              <NoAccountTextContainer>
                <FixedTitle
                  varient="Fixed14Regular"
                  color={!isMobile ? brand.white : greyScaleColour.grey100}
                >
                  {messages?.sidePanel?.needHelp}
                </FixedTitle>
                <FixedTitle
                  varient={isMobile ? "Fixed14Medium" : "Fixed14Bold"}
                  onClick={() => reduxDispatch(push(routes.getInTouch))}
                  color={!isMobile ? brand.white : brand?.secondaryMain}
                  style={{ cursor: "pointer" }}
                >
                  {messages?.sidePanel?.getInTouch}
                </FixedTitle>
              </NoAccountTextContainer>
              {/*
                // TODO: Temporarily hidden due to TPHP-10248
                // as the FP Mobile App is missing a lot of features that FP WEB has
                <DownloadAppBtn
                  appStoreLink={downloadAppLink.appStore}
                  playStoreLink={downloadAppLink.playStore}
                />
                
              */}
            </StyledSidePanelLowerTextMobile>
          </StyledContentContainer>
        </StyledFormContainer>
        <SidePanel />
      </StyledScreenWrapper>
      <Modal
        show={otpFormVisibility}
        heading="Confirm OTP"
        onClose={hideOtpForm}
        fitContent
      >
        <ConfirmOtpForm
          onCancel={hideOtpForm}
          error={error}
          setError={setError}
          onSubmit={(confirmationCode: string) => {
            handleOtpConfirmation(confirmationCode);
          }}
          username={username}
        />
      </Modal>

      {/* TOTP Modal */}
      <Modal
        show={totpFormVisibility}
        heading="Enter your security code"
        subHeading="We need a code from your phone to finish signing in."
        subText="There is no need to scan the QR code again, you are already registered."
        onClose={hideTotpForm}
        noCloseIcon={true}
        fitContent
        maxWidth={isMobile ? "auto" : "480px"}
      >
        <ConfirmOtpForm
          onCancel={hideTotpForm}
          error={error}
          setError={setError}
          onSubmit={(confirmationCode: string) => {
            handleMfaVerification(confirmationCode, totpFormMetadata?.data);
          }}
          username={username}
          isResend={false}
          isMfa
        />
      </Modal>

      {/* SMS Modal */}
      <Modal
        show={smsFormVisibility}
        heading="Enter your security code"
        subHeading="We need a code from your phone to finish signing in."
        onClose={hideSmsForm}
        noCloseIcon={true}
        fitContent
        maxWidth={isMobile ? "auto" : "480px"}
      >
        <ConfirmOtpForm
          onCancel={hideSmsForm}
          error={error}
          setError={setError}
          onSubmit={(confirmationCode: string) => {
            handleMfaVerification(confirmationCode, smsFormMetadata?.data);
          }}
          username={username}
          isResend={false}
          isSms
        />
      </Modal>
    </Container>
  );
};

export default Login;
